import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Crown, 
  BookOpen, 
  Music, 
  Heart, 
  Download, 
  Phone, 
  Instagram, 
  Youtube,
  ExternalLink,
  Sparkles,
  Globe,
  Quote,
  RefreshCw
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { getRandomPassage } from '../data/passages';
import { getRandomVideo } from '../data/videos';
import { getRandomPhoto } from '../data/photos';
import Guestbook from './Guestbook';

const LandingPage = () => {
  const [activeBook, setActiveBook] = useState(0);
  const [featuredPassage, setFeaturedPassage] = useState(null);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [featuredPhoto, setFeaturedPhoto] = useState(null);

  useEffect(() => {
    // Get a random passage, video, and photo when component mounts
    setFeaturedPassage(getRandomPassage());
    setFeaturedVideo(getRandomVideo());
    setFeaturedPhoto(getRandomPhoto());
  }, []);

  const books = [
    {
      title: "The Mushroom Seamstress",
      pages: "820 pages",
      image: "https://www.artsetfree.com/2025/TOP-TheMushroomSeamstress-BochSaga-ALoveLetterToHumanity-2025-4.jpg",
      downloadLink: "https://www.artsetfree.com/TheMushroomSeamstress[EditionMyCecilia]-WwwArtSetFreeCom.pdf",
      buyLink: "https://www.lulu.com/search?contributor=King+Spiros+of+Plomari&page=1&pageSize=10&adult_audience_rating=00"
    },
    {
      title: "The Chymical Wedding",
      pages: "The Pink Bible of Plomari",
      image: "https://www.artsetfree.com/image/BookIntro_TheChymicalWeddingAtLast.jpg",
      downloadLink: "https://www.artsetfree.com/TheChymicalWedding-TheWhiteSwan-EditionTheFirstSpringEver-WwwArtSetFreeCom.pdf",
      buyLink: "https://www.lulu.com/en/us/shop/king-spiros-of-plomari-/the-chymical-wedding/hardcover/product-189g4p4w.html"
    },
    {
      title: "The Cogan Dynasty",
      pages: "A Love Letter To Humanity",
      image: "https://www.artsetfree.com/image/BookIntro_TheCoganDynasty.jpg",
      downloadLink: "https://www.artsetfree.com/TheCoganDynasty-by-SpirosAndCeciliaCoganAndButterfly-WwwArtSetFreeCom.pdf",
      buyLink: "http://www.lulu.com/shop/king-spiros-of-plomari/the-cogan-dynasty/hardcover/product-23799931.html"
    }
  ];

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="crown-icon-wrapper">
            <Crown className="crown-icon" />
          </div>
          <h1 className="hero-title">
            The Eternal Kingdom of Plomari
          </h1>
          <p className="hero-subtitle">
            King Spiros of Plomari
          </p>
          <p className="hero-description">
            A visionary artist, author and musician who founded the Eternal Kingdom of Plomari - 
            a living work of art that blends music, literature, and philosophy into one vast creation.
          </p>
          <div className="hero-buttons">
            <Button className="cta-primary" size="lg">
              <Download className="mr-2" />
              Download Free Books
            </Button>
            <Button variant="outline" className="cta-secondary" size="lg">
              <Globe className="mr-2" />
              Enter the Kingdom
            </Button>
          </div>
          <div className="hero-quote">
            "YOU ARE FREE NOW"
          </div>
        </div>
      </section>

      {/* Featured Passage */}
      {featuredPassage && (
        <section className="featured-passage-section">
          <div className="container">
            <div className="featured-passage-content">
              <div className="passage-icon-wrapper">
                <Quote className="passage-icon" />
              </div>
              <p className="passage-text">
                "{featuredPassage.text}"
              </p>
              <p className="passage-source">
                — {featuredPassage.source}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Featured Video */}
      {featuredVideo && (
        <section className="featured-video-section">
          <div className="container">
            <div className="featured-video-content">
              <div className="video-header">
                <Music className="video-icon" />
                <h2 className="video-title">Experience the Sound of Plomari</h2>
                <p className="video-subtitle">
                  Random selection from King Spiros' musical journey
                </p>
              </div>
              <div className="video-player-wrapper">
                <iframe
                  width="100%"
                  height="500"
                  src={`https://www.youtube.com/embed/${featuredVideo.videoId}`}
                  title={featuredVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="featured-video-iframe"
                ></iframe>
                <p className="video-caption">{featuredVideo.title}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured Photo */}
      {featuredPhoto && (
        <section className="featured-photo-section">
          <div className="container">
            <div className="featured-photo-content">
              <div className="photo-header">
                <Sparkles className="photo-icon" />
                <h2 className="photo-title">Visual Journey Through Plomari</h2>
                <p className="photo-subtitle">
                  Random imagery from the eternal Kingdom
                </p>
              </div>
              <div className="photo-display-wrapper">
                <img 
                  src={featuredPhoto.url} 
                  alt={featuredPhoto.title}
                  className="featured-photo-image"
                />
                <div className="photo-caption-wrapper">
                  <h3 className="photo-caption-title">{featuredPhoto.title}</h3>
                  <p className="photo-caption-description">{featuredPhoto.description}</p>
                </div>
              </div>
              <button 
                className="show-new-photo-btn"
                onClick={() => setFeaturedPhoto(getRandomPhoto())}
              >
                <RefreshCw size={18} />
                Show New Photo
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Guestbook Section */}
      <Guestbook />

      {/* Mission Statement */}
      <section className="mission-section">
        <div className="container">
          <div className="mission-content">
            <Sparkles className="mission-icon" />
            <h2 className="section-title">Welcome to Plomari</h2>
            <p className="mission-text">
              The eternal Kingdom of Plomari, where the past, present and future are in love. 
              Our mission? Simply put: To help set humanity free and remind the world how beautiful life can be.
            </p>
            <p className="mission-quote">
              "The Kingdom of Plomari is your world. The Plomarians are your people. 
              You can live for yourself today, or help build tomorrow for everyone."
            </p>
          </div>
        </div>
      </section>

      {/* Books Section */}
      <section className="books-section">
        <div className="container">
          <div className="section-header">
            <BookOpen className="section-icon" />
            <h2 className="section-title">A Love Letter To Humanity</h2>
            <p className="section-subtitle">
              4,000-page book series forming the heart of Plomari - Download free ebooks or own royal hardcover editions
            </p>
          </div>
          
          <div className="books-grid">
            {books.map((book, index) => (
              <Card key={index} className="book-card">
                <CardContent className="book-card-content">
                  <div className="book-image-wrapper">
                    <img src={book.image} alt={book.title} className="book-image" />
                  </div>
                  <h3 className="book-title">{book.title}</h3>
                  <p className="book-pages">{book.pages}</p>
                  <div className="book-actions">
                    <Button className="book-btn-primary" asChild>
                      <a href={book.downloadLink} target="_blank" rel="noopener noreferrer">
                        <Download className="mr-2" size={18} />
                        Download Free
                      </a>
                    </Button>
                    <Button variant="outline" className="book-btn-secondary" asChild>
                      <a href={book.buyLink} target="_blank" rel="noopener noreferrer">
                        Buy Hardcover
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="more-books">
            <Button variant="outline" size="lg" asChild>
              <a href="https://www.artsetfree.com/42.html">
                View All Books <ExternalLink className="ml-2" size={18} />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Music Section */}
      <section className="music-section">
        <div className="container">
          <div className="section-header">
            <Music className="section-icon" />
            <h2 className="section-title">SISSY COGAN</h2>
            <p className="section-subtitle">
              The music of the eternal Kingdom - Plomari Radio Free broadcasting since 2005
            </p>
          </div>

          <div className="music-grid">
            <div className="music-featured">
              <img 
                src="https://images.unsplash.com/photo-1767403154501-a677bb969ba6?w=800" 
                alt="Music of Plomari" 
                className="music-image"
              />
              <div className="music-overlay">
                <h3>Experience the Sound of Plomari</h3>
                <p>Electronic, atmospheric, and spiritually awakening</p>
              </div>
            </div>

            <div className="youtube-embeds">
              <div className="youtube-wrapper">
                <iframe
                  width="100%"
                  height="200"
                  src="https://www.youtube.com/embed/pwX-2FhN_7U"
                  title="SISSY COGAN - Flee From The One Named Queen Cecilia Cogan"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="youtube-wrapper">
                <iframe
                  width="100%"
                  height="200"
                  src="https://www.youtube.com/embed/8vePNhiEIfE"
                  title="SISSY COGAN - Remember What It Means To Be Free"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="philosophy-section">
        <div className="container">
          <div className="philosophy-content">
            <h2 className="section-title">The Wind That Topples the House of Cards</h2>
            <div className="philosophy-text">
              <p>
                For millennia, human history has been built like a fragile house of cards—a precarious 
                structure of traditions, systems, and beliefs stacked atop one another, vulnerable to 
                the slightest disruption.
              </p>
              <p className="highlight-text">
                "Human history is like a house of cards, and Plomari is designed to blow it down. 
                This doesn't mean the world will vanish or end; it simply marks the end of bullshit 
                and the beginning of paradise."
              </p>
              <p className="author-text">~ King Spiros of Plomari</p>
            </div>

            <Accordion type="single" collapsible className="philosophy-accordion">
              <AccordionItem value="item-1">
                <AccordionTrigger>What is the Kingdom of Plomari?</AccordionTrigger>
                <AccordionContent>
                  The Kingdom of Plomari is a living work of art that blends music, literature, and 
                  philosophy. It's a sanctuary for freedom, creativity, and spiritual exploration. 
                  Plomari isn't fantasy - it's a real, radiant movement of art, freedom and soul.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2">
                <AccordionTrigger>Who is King Spiros of Plomari?</AccordionTrigger>
                <AccordionContent>
                  King Spiros of Plomari is a visionary artist, author and musician. His 4,000-page 
                  book series "A Love Letter To Humanity" and the music of his band SISSY COGAN form 
                  the beating heart of the Kingdom. Some call him crazy, only because he reminds people 
                  that they have wings and can fly.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3">
                <AccordionTrigger>How can I explore Plomari?</AccordionTrigger>
                <AccordionContent>
                  To explore Plomari is free. Read the ebooks, listen to the music, explore the website, 
                  and be part of the Kingdom. Plomari can be whatever you want or need right now - a 
                  timeless sanctuary, a spiritual refuge, or a celebration of life's beauty.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4">
                <AccordionTrigger>The Sacred Knowledge</AccordionTrigger>
                <AccordionContent>
                  One reason we created The Kingdom of Plomari is to make sure the sacred knowledge of 
                  the magic mushroom is never lost. Plomari is a culture of remembrance, keeping the 
                  Mystery alive for future generations.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Donation Section */}
      <section className="donation-section">
        <div className="container">
          <Card className="donation-card">
            <CardContent className="donation-content">
              <Heart className="donation-icon" />
              <h2 className="donation-title">Support the Kingdom</h2>
              <p className="donation-text">
                Mostly everything is free in Plomari. But running the eternal Kingdom is expensive 
                and challenging. Your donation helps keep the magic alive and supports the Queen of 
                the Animals in her mission to help wildlife worldwide.
              </p>
              <Button className="donation-btn" size="lg" asChild>
                <a href="https://gofund.me/4c6b9be8" target="_blank" rel="noopener noreferrer">
                  <Heart className="mr-2" />
                  Donate to Plomari
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-content">
            <h2 className="section-title">Connect with King Spiros</h2>
            <p className="contact-description">
              Private phonecalls available for life coaching, collaboration, or conversation about 
              the Kingdom of Plomari. Free for the first few minutes, then $60/hour.
            </p>
            
            <div className="contact-methods">
              <div className="contact-item">
                <Phone className="contact-icon" />
                <div>
                  <p className="contact-label">Phone (Sweden)</p>
                  <a href="tel:+46725202623" className="contact-link">+46 (0)72 520 26 23</a>
                </div>
              </div>
              
              <div className="contact-item">
                <Instagram className="contact-icon" />
                <div>
                  <p className="contact-label">Instagram</p>
                  <a 
                    href="https://www.instagram.com/kingspirosofplomari/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="contact-link"
                  >
                    @kingspirosofplomari
                  </a>
                </div>
              </div>
              
              <div className="contact-item">
                <Youtube className="contact-icon" />
                <div>
                  <p className="contact-label">YouTube</p>
                  <a 
                    href="https://www.youtube.com/channel/UCMw4s7JLSDhNlq4Ul93ljug" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="contact-link"
                  >
                    King Spiros of Plomari
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <Crown size={32} />
              <span>Kingdom of Plomari</span>
            </div>
            <p className="footer-text">
              ArtSetFree.com - Official Website of King Spiros of Plomari
            </p>
            <p className="footer-quote">
              "Breathe Plomari Eternity, You'll never breathe alone"
            </p>
            <p className="footer-notice">
              This website is 18+
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
// Force recompile Sun Feb 15 09:02:34 UTC 2026
